import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AlarmClock, Check, CheckCircle2, ExternalLink, HelpCircle, Link2, Lock, MoreHorizontal, Plus, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import type { RequestAction, ProjectMovePreview, RequestView } from '@vwt/contracts';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  Input,
  NativeSelect,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@vwt/ui';
import { api, newIdempotencyKey, POLL_ACTIVE, useApiMutation, useApiQuery } from '../../lib/api.js';
import { useConflictAwareSave } from '../../components/conflict-dialog.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { KeyLabel, PhaseBadge, PriorityLabel, RequestStatusBadge, VisibilityBadge } from '../../components/badges.js';
import { PrincipalPicker, PropertyRow, QueryState, SectionTitle, TimeAgo, UserChip } from '../../components/common.js';
import { CommentsPanel } from '../../components/comments.js';
import { AttachmentsPanel } from '../../components/attachments.js';
import { MarkdownEditor } from '../../components/markdown.js';
import { OriginalLanguage, TranslatableMarkdown, TranslatableText, TranslateToggle, TranslationScope } from '../../components/translation.js';
import { VisibilityDialog } from '../../components/visibility.js';
import { formatDateTime } from '../../lib/format.js';
import { CreateWorkDialog } from '../work/create-work.js';
import { AiDuplicateCheck, TriageAssist } from './triage-assist.js';
import { ItemRef } from '../../components/item-ref.js';
import { DocumentBacklinks } from '../documents/document-backlinks.js';

const STAGES = ['NOT_STARTED', 'IN_PROGRESS', 'IN_REVIEW', 'AWAITING_DEPLOYMENT', 'DELIVERED'] as const;

/** Request detail by display key (REQ-3) or UUID. */
export function RequestDetail({ id, inDrawer = false }: { id: string; inDrawer?: boolean }) {
  return <ItemRef value={id} kind="REQUEST">{(uuid) => <RequestDetailById id={uuid} inDrawer={inDrawer} />}</ItemRef>;
}

function RequestDetailById({ id, inDrawer }: { id: string; inDrawer: boolean }) {
  const { t } = useTranslation();
  const query = useApiQuery('getRequest', { params: { id } }, { refetchInterval: POLL_ACTIVE });
  return (
    <QueryState query={query} skeleton={<div className="flex flex-col gap-3"><Skeleton className="h-5 w-24" /><Skeleton className="h-7 w-3/4" /><Skeleton className="h-40 w-full" /></div>}>
      {query.data ? (
        <TranslationScope key={query.data.data.id}>
          <RequestBody request={query.data.data} inDrawer={inDrawer} />
        </TranslationScope>
      ) : (
        <p className="p-6 text-sm">{t('errors.notFound')}</p>
      )}
    </QueryState>
  );
}

function RequestBody({ request, inDrawer }: { request: RequestView; inDrawer: boolean }) {
  const { t } = useTranslation();
  const can = (a: string) => request.availableActions.includes(a);
  const [dialog, setDialog] = useState<RequestAction | null>(null);
  const [dialogCanonical, setDialogCanonical] = useState<{ id: string; title: string; key: string } | null>(null);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const transitions = request.availableActions.filter((a) => a.startsWith('transition:')).map((a) => a.slice(11) as RequestAction);
  const primaryOrder: RequestAction[] = ['accept', 'answer_information', 'accept_correction', 'fulfill_after_review', 'fulfill', 'ask_information', 'reconsider'];
  const primary = primaryOrder.filter((a) => transitions.includes(a)).slice(0, 2);
  const secondary = transitions.filter((a) => !primary.includes(a) && a !== 'report_persistence' && a !== 'submit');
  const update = useApiMutation('updateRequest', { invalidate: ['getRequest', 'listRequests'] });
  const follow = useApiMutation('followRequest', { invalidate: ['getRequest'] });
  const quick = useApiMutation('transitionRequest', { invalidate: ['getRequest', 'listRequests', 'getRequestDecisions', 'listComments'] });

  // Actions without inputs run directly; the others open a dialog that collects what the guard needs.
  const startAction = async (action: RequestAction) => {
    if (!SIMPLE_REQUEST_ACTIONS.includes(action)) {
      setDialog(action);
      return;
    }
    try {
      await quick.mutateAsync({ params: { id: request.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: request.version, action } as never });
      toast.success(t(`actions.request.${action}`));
    } catch (error) {
      toastError(error);
    }
  };

  const { patch, dialog: conflictDialog } = useConflictAwareSave(
    request,
    async () => (await api.call('getRequest', { params: { id: request.id } }))!.data,
    (body, version) => update.mutateAsync({ params: { id: request.id }, body: { ...body, expectedVersion: version } as never, idempotencyKey: newIdempotencyKey() }),
  );

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <div className="border-b border-border px-5 pt-4 pb-3">
        <div className="mb-2 flex flex-wrap items-center gap-2 pr-8">
          <KeyLabel value={request.key} />
          <VisibilityBadge visibility={request.visibility} />
          <Badge tone="outline">{t(`status.channel.${request.sourceChannel}`)}</Badge>
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => void follow.mutateAsync({ params: { id: request.id }, body: { following: !request.following } }).catch(toastError)} aria-pressed={request.following}>
              {request.following ? <Check /> : <Plus />} {request.following ? t('requests.detail.following') : t('requests.detail.followers')}
            </Button>
            <TranslateToggle />
            <Button
              variant="ghost"
              size="iconSm"
              aria-label={t('common.copyLink')}
              onClick={() => {
                void navigator.clipboard.writeText(request.canonicalUrl);
                toast.success(t('common.linkCopied'));
              }}
            >
              <Link2 />
            </Button>
            {inDrawer ? (
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/requests/${request.key}`}>
                  <ExternalLink /> {t('common.openFullPage')}
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
        <TranslatableText as="h1" className="text-lg leading-snug font-semibold" entityKind="REQUEST" entityId={request.id} field="title" source={request.title || '—'} sourceLocale={request.originalLocale} contentRevision={request.contentRevision} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <RequestStatusBadge status={request.status} />
          <PriorityLabel priority={request.priority} />
          {request.snoozedUntil && new Date(request.snoozedUntil) > new Date() ? (
            <Badge tone="outline">
              <AlarmClock /> {t('requests.snooze.badge', { time: formatDateTime(request.snoozedUntil) })}
            </Badge>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {can('snooze') ? <SnoozeMenu request={request} /> : null}
            {primary.map((action, i) => (
              <Button key={action} size="sm" variant={i === 0 ? 'primary' : 'secondary'} loading={quick.isPending && quick.variables?.body?.action === action} disabled={quick.isPending} onClick={() => void startAction(action)}>
                {t(`actions.request.${action}`)}
              </Button>
            ))}
            {secondary.length || can('changeVisibility') ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="iconSm" variant="ghost" aria-label="More">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {secondary.map((action) => (
                    <DropdownMenuItem key={action} destructive={['reject', 'cancel'].includes(action)} onSelect={() => void startAction(action)}>
                      {t(`actions.request.${action}`)}
                    </DropdownMenuItem>
                  ))}
                  {can('changeVisibility') ? (
                    <DropdownMenuItem onSelect={() => setVisibilityOpen(true)}>
                      <Lock /> {t('visibility.title')}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto @4xl:grid-cols-[minmax(0,1fr)_288px]">
        <div className="flex min-w-0 flex-col gap-6 px-5 py-4">
          {request.status === 'NEEDS_INFORMATION' && request.pendingQuestion ? <PendingQuestion request={request} canAnswer={can('transition:answer_information')} /> : null}
          {can('confirm') ? <ConfirmationPanel request={request} /> : null}
          <FunctionalProgress request={request} />
          <section className="flex flex-col gap-4">
            <Block title={t('requests.detail.problem')} entity={request} field="problemMarkdown" source={request.problemMarkdown} />
            <Block title={t('requests.detail.currentBehavior')} entity={request} field="currentBehavior" source={request.currentBehavior} />
            <Block title={t('requests.detail.expectedOutcome')} entity={request} field="expectedOutcome" source={request.expectedOutcome} />
            {request.impactUnknown ? (
              <div>
                <SectionTitle>{t('requests.detail.impact')}</SectionTitle>
                <p className="text-sm text-fg-subtle">{t('requests.detail.impactUnknown')}</p>
              </div>
            ) : (
              <Block title={t('requests.detail.impact')} entity={request} field="impact" source={request.impact} />
            )}
            {request.reproduction ? (
              <div>
                <SectionTitle>{t('requests.detail.reproduction')}</SectionTitle>
                <p className="text-sm whitespace-pre-wrap">{request.reproduction}</p>
              </div>
            ) : null}
          </section>
          {request.duplicateOf ? (
            <p className="text-sm">
              {t('requests.detail.duplicateOf')}{' '}
              <Link className="font-medium hover:underline" to={`/requests/${request.duplicateOf.key}`}>
                {request.duplicateOf.key} · {request.duplicateOf.title}
              </Link>
            </p>
          ) : null}
          {request.decisionReason ? (
            <div className="rounded-[var(--radius)] border border-border bg-surface-2 px-3 py-2 text-sm">
              <span className="text-xs font-medium text-fg-subtle">{t('requests.detail.decisionReason')}: </span>
              {request.decisionReason}
            </div>
          ) : null}
          {can('transition:mark_duplicate') ? (
            <DuplicateSuggestions
              request={request}
              onMark={(candidate) => {
                setDialogCanonical(candidate);
                setDialog('mark_duplicate');
              }}
            />
          ) : null}
          <AttachmentsPanel parentKind="REQUEST" parentId={request.id} />
          <DocumentBacklinks targetKind="REQUEST" targetId={request.id} />
          <Tabs defaultValue="comments">
            <TabsList>
              <TabsTrigger value="comments">{t('requests.detail.comments')}</TabsTrigger>
              <TabsTrigger value="work">{t('requests.detail.linkedWork')}</TabsTrigger>
              <TabsTrigger value="decisions">{t('requests.detail.decisions')}</TabsTrigger>
            </TabsList>
            <TabsContent value="comments" className="pt-4">
              <div id="comments" className="scroll-mt-4">
                <CommentsPanel parentKind="REQUEST" parentId={request.id} />
              </div>
            </TabsContent>
            <TabsContent value="work" className="pt-4">
              <LinkedWork request={request} />
            </TabsContent>
            <TabsContent value="decisions" className="pt-4">
              <Decisions id={request.id} />
            </TabsContent>
          </Tabs>
        </div>
        <aside className="border-t border-border bg-surface-2 px-5 py-4 @4xl:border-t-0 @4xl:border-l">
          <SectionTitle>{t('requests.detail.triage')}</SectionTitle>
          <dl>
            <PropertyRow label={t('requests.columns.author')}>
              <UserChip user={request.author} link />
            </PropertyRow>
            <PropertyRow label={t('requests.columns.assignee')}>
              {can('assign') ? <PrincipalPicker label={t('requests.columns.assignee')} value={request.assignee ? { id: request.assignee.id, name: request.assignee.displayName } : null} onChange={(p) => void patch({ assigneeId: p?.id ?? null })} /> : <UserChip user={request.assignee} link />}
            </PropertyRow>
            <PropertyRow label={t('requests.columns.priority')}>
              {can('prioritize') ? (
                <NativeSelect className="h-7" value={request.priority} onChange={(e) => void patch({ priority: e.target.value })} aria-label={t('requests.columns.priority')}>
                  {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
                    <option key={p} value={p}>
                      {t(`status.priority.${p}`)}
                    </option>
                  ))}
                </NativeSelect>
              ) : (
                <PriorityLabel priority={request.priority} />
              )}
            </PropertyRow>
            <PropertyRow label={t('requests.detail.triageTeam')}>{request.triageTeam?.name ?? '—'}</PropertyRow>
            <PropertyRow label={t('languageCorrection.label')}>
              <OriginalLanguage value={request.originalLocale} canEdit={can('edit') || can('assign')} onChange={(originalLocale) => void patch({ originalLocale })} />
            </PropertyRow>
            <PropertyRow label={t('work.columns.project')}>
              {can('assign') ? <ProjectMove request={request} onMove={(projectId) => patch({ projectId })} /> : request.project ? <Link className="hover:underline" to={`/projects/${request.project.id}`}>{request.project.name}</Link> : '—'}
            </PropertyRow>
            <PropertyRow label={t('requests.detail.source')}>{t(`status.channel.${request.sourceChannel}`)}</PropertyRow>
            {request.submittedAt ? <PropertyRow label={t('requests.columns.updated')}><span className="text-xs" title={formatDateTime(request.updatedAt)}><TimeAgo iso={request.updatedAt} /></span></PropertyRow> : null}
            {request.currentConfirmation ? (
              <PropertyRow label={t('requests.confirm.generation', { generation: request.currentConfirmation.generation })}>
                <Badge tone={request.currentConfirmation.outcome === 'CONFIRMED' ? 'success' : request.currentConfirmation.outcome === 'PERSISTS' ? 'danger' : 'neutral'}>{t(`status.confirmation.${request.currentConfirmation.outcome}`)}</Badge>
              </PropertyRow>
            ) : null}
          </dl>
          {(can('prioritize') || can('assign')) && ['SUBMITTED', 'NEEDS_INFORMATION', 'NEEDS_ATTENTION', 'ACCEPTED'].includes(request.status) ? <TriageAssist request={request} onPatch={patch} /> : null}
        </aside>
      </div>
      {conflictDialog}
      {dialog ? <RequestTransitionDialog request={request} action={dialog} initialCanonical={dialogCanonical} onClose={() => { setDialog(null); setDialogCanonical(null); }} /> : null}
      <VisibilityDialog open={visibilityOpen} onOpenChange={setVisibilityOpen} resource={{ kind: 'request', id: request.id }} />
    </div>
  );
}

/** Next local time at hour:00 after adding days (snooze presets). */
function at(daysAhead: number, hour = 9): Date {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function nextMonday(): Date {
  const days = ((8 - new Date().getDay()) % 7) || 7;
  return at(days);
}

/** Triage snooze: presets, a custom date, or wake the request up now. */
function SnoozeMenu({ request }: { request: RequestView }) {
  const { t } = useTranslation();
  const snooze = useApiMutation('snoozeRequest', { invalidate: ['getRequest', 'listRequests'] });
  const [custom, setCustom] = useState(false);
  const [value, setValue] = useState('');
  const snoozed = !!request.snoozedUntil && new Date(request.snoozedUntil) > new Date();
  const run = async (until: Date | null) => {
    try {
      await snooze.mutateAsync({ params: { id: request.id }, body: { expectedVersion: request.version, until: until ? until.toISOString() : null }, idempotencyKey: newIdempotencyKey() });
      toast.success(until ? t('requests.snooze.snoozedToast', { time: formatDateTime(until.toISOString()) }) : t('requests.snooze.wokenToast'));
      setCustom(false);
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="secondary" loading={snooze.isPending}>
            <AlarmClock /> {t('requests.snooze.action')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => void run(at(1))}>{t('requests.snooze.tomorrow')}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void run(nextMonday())}>{t('requests.snooze.nextWeek')}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void run(at(14))}>{t('requests.snooze.inTwoWeeks')}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setCustom(true)}>{t('requests.snooze.custom')}</DropdownMenuItem>
          {snoozed ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void run(null)}>{t('requests.snooze.wake')}</DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={custom} onOpenChange={setCustom}>
        <DialogContent title={t('requests.snooze.title')} description={t('requests.snooze.hint')} closeLabel={t('common.close')}>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (value) void run(new Date(value));
            }}
          >
            <Field label={t('requests.snooze.until')} htmlFor="snooze-until" required>
              <Input id="snooze-until" type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} required />
            </Field>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCustom(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={snooze.isPending} disabled={!value}>
                {t('requests.snooze.action')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Block({ title, entity, field, source }: { title: string; entity: RequestView; field: 'problemMarkdown' | 'currentBehavior' | 'expectedOutcome' | 'impact'; source: string | null }) {
  if (!source?.trim()) return null;
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <TranslatableMarkdown entityKind="REQUEST" entityId={entity.id} field={field} source={source} sourceLocale={entity.originalLocale} contentRevision={entity.contentRevision} />
    </div>
  );
}

/** Requester-safe functional progress: no hidden task titles, keys, counts or evidence. */
function FunctionalProgress({ request }: { request: RequestView }) {
  const { t } = useTranslation();
  const stage = request.fulfillment.stage;
  if (['DRAFT', 'SUBMITTED', 'NEEDS_INFORMATION', 'REJECTED', 'CANCELED'].includes(request.status)) return null;
  if (request.status === 'NEEDS_ATTENTION') {
    return (
      <Card className="border-danger/30 bg-danger-soft/30 px-4 py-3">
        <SectionTitle>{t('requests.detail.progress')}</SectionTitle>
        <p className="text-sm">{t('status.request.NEEDS_ATTENTION')} · {t('status.confirmation.PERSISTS')}</p>
      </Card>
    );
  }
  const stages: string[] = request.activation.required ? [...STAGES.slice(0, 4), 'AWAITING_ACTIVATION', 'DELIVERED'] : [...STAGES];
  const index = stages.indexOf(stage);
  return (
    <Card className="px-4 py-3">
      <SectionTitle>{t('requests.detail.progress')}</SectionTitle>
      {stage === 'NONE_LINKED' ? (
        <p className="text-sm text-fg-muted">{t('status.fulfillment.NONE_LINKED')}</p>
      ) : (
        <ol className="flex flex-wrap items-center gap-1.5" aria-label={t('requests.detail.progress')}>
          {stages.map((s, i) => (
            <li key={s} className="flex items-center gap-1.5">
              <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${i < index ? 'bg-success-soft text-success' : i === index ? 'bg-accent text-accent-fg' : 'bg-muted text-fg-subtle'}`} aria-current={i === index ? 'step' : undefined}>
                {i < index ? <Check className="size-3" /> : null}
                {t(`status.fulfillment.${s}`)}
              </span>
              {i < stages.length - 1 ? <span className="h-px w-3 bg-border-strong" aria-hidden /> : null}
            </li>
          ))}
        </ol>
      )}
      {request.fulfillment.hasHiddenWork ? <p className="mt-2 flex items-center gap-1 text-xs text-fg-subtle"><Lock className="size-3" />{t('requests.detail.hiddenWork')}</p> : null}
      <ActivationPanel request={request} />
    </Card>
  );
}

/**
 * Request-level activation (Section 9.3): shipping code may not make a change available yet. Triagers
 * require it with a note, then record who observed it live and the evidence; it can also be waived.
 */
function ActivationPanel({ request }: { request: RequestView }) {
  const { t } = useTranslation();
  const canManage = request.availableActions.includes('assign');
  const save = useApiMutation('setRequestActivation', { invalidate: ['getRequest', 'listRequests'] });
  const [mode, setMode] = useState<'REQUIRE' | 'OBSERVE' | 'WAIVE' | null>(null);
  const [text, setText] = useState('');
  const activation = request.activation;
  if (!activation.required && !canManage) return null;
  const submit = async () => {
    if (!mode || !text.trim()) return;
    const body = mode === 'REQUIRE' ? { action: mode, expectedVersion: request.version, note: text.trim() } : mode === 'OBSERVE' ? { action: mode, expectedVersion: request.version, evidenceMarkdown: text.trim() } : { action: mode, expectedVersion: request.version, reason: text.trim() };
    try {
      await save.mutateAsync({ params: { id: request.id }, body: body as never, idempotencyKey: newIdempotencyKey() });
      toast.success(t('common.changesSaved'));
      setMode(null);
      setText('');
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <div className="mt-3 border-t border-border pt-3 text-sm" data-testid="activation-panel">
      {activation.required ? (
        activation.activatedAt ? (
          <p className="flex items-start gap-1.5 text-success">
            <Check className="mt-0.5 size-3.5 shrink-0" />
            <span>{t('requests.activation.observed', { name: activation.activatedBy?.displayName ?? '—', time: formatDateTime(activation.activatedAt) })}</span>
          </p>
        ) : (
          <p className="text-fg-muted">
            <span className="font-medium text-fg">{t('requests.activation.pending')}</span> {activation.note}
          </p>
        )
      ) : (
        <p className="text-xs text-fg-subtle">{t('requests.activation.notRequired')}</p>
      )}
      {canManage ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {!activation.required ? (
            <Button size="sm" variant="ghost" onClick={() => setMode('REQUIRE')}>
              {t('requests.activation.require')}
            </Button>
          ) : null}
          {activation.required && !activation.activatedAt ? (
            <>
              <Button size="sm" variant="secondary" onClick={() => setMode('OBSERVE')}>
                {t('requests.activation.record')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode('WAIVE')}>
                {t('requests.activation.waive')}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {mode ? (
        <Dialog open onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent title={t(`requests.activation.${mode === 'REQUIRE' ? 'require' : mode === 'OBSERVE' ? 'record' : 'waive'}`)} description={t(`requests.activation.${mode === 'REQUIRE' ? 'requireHint' : mode === 'OBSERVE' ? 'recordHint' : 'waiveHint'}`)} closeLabel={t('common.close')}>
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <Field label={t(`requests.activation.${mode === 'REQUIRE' ? 'noteLabel' : mode === 'OBSERVE' ? 'evidenceLabel' : 'reasonLabel'}`)} required>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={mode === 'OBSERVE' ? 8000 : 1000} autoFocus />
              </Field>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="primary" loading={save.isPending} disabled={!text.trim()}>
                  {t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function PendingQuestion({ request, canAnswer }: { request: RequestView; canAnswer: boolean }) {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState('');
  const transition = useApiMutation('transitionRequest', { invalidate: ['getRequest', 'listComments', 'listRequests'] });
  return (
    <Card className="border-warning/40 bg-warning-soft/40 px-4 py-3">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-warning">
        <HelpCircle className="size-3.5" /> {t('requests.detail.pendingQuestion')}
      </p>
      <p className="text-sm">{request.pendingQuestion}</p>
      {canAnswer ? (
        <div className="mt-3 flex flex-col gap-2">
          <MarkdownEditor value={answer} onChange={setAnswer} minRows={3} placeholder={t('requests.detail.answerPlaceholder')} />
          <Button
            variant="primary"
            size="sm"
            className="self-end"
            disabled={!answer.trim()}
            loading={transition.isPending}
            onClick={async () => {
              try {
                await transition.mutateAsync({ params: { id: request.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: request.version, action: 'answer_information', answerMarkdown: answer } });
                setAnswer('');
              } catch (error) {
                toastError(error);
              }
            }}
          >
            {t('actions.request.answer_information')}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Requester confirmation bound to the displayed fulfillment generation. Explicit authenticated
 * actions only: opening an email link never confirms or reopens (Section 10.2).
 */
/**
 * Moving a request between projects changes who can read it (Section 4.4). The move is previewed:
 * restriction changes and people who would lose access are shown before it is applied.
 */
function ProjectMove({ request, onMove }: { request: RequestView; onMove: (projectId: string | null) => Promise<boolean> }) {
  const { t } = useTranslation();
  const projects = useApiQuery('listProjects', { query: {} });
  const [target, setTarget] = useState<{ id: string | null; name: string } | null>(null);
  const [preview, setPreview] = useState<ProjectMovePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const choose = async (value: string) => {
    const id = value || null;
    const name = id ? (projects.data?.data.find((pr) => pr.id === id)?.name ?? '') : t('requests.move.noProject');
    try {
      const result = await api.call('previewRequestProjectMove', { params: { id: request.id }, body: { projectId: id } });
      setTarget({ id, name });
      setPreview(result!.data);
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <>
      <NativeSelect className="h-7" value={request.project?.id ?? ''} aria-label={t('work.columns.project')} onChange={(e) => void choose(e.target.value)}>
        <option value="">{t('requests.move.noProject')}</option>
        {(projects.data?.data ?? []).map((pr) => (
          <option key={pr.id} value={pr.id}>
            {pr.name}
          </option>
        ))}
      </NativeSelect>
      {target && preview ? (
        <Dialog open onOpenChange={(o) => !o && (setTarget(null), setPreview(null))}>
          <DialogContent title={t('requests.move.title', { name: target.name })} description={t('requests.move.hint')} closeLabel={t('common.close')}>
            <div className="flex flex-col gap-2 text-sm" data-testid="move-preview">
              <p>{preview.restrictedAfter ? t('requests.move.restrictedAfter') : t('requests.move.companyAfter')}</p>
              {preview.losesAccess.length ? (
                <div>
                  <p className="font-medium text-warning">{t('requests.move.losesAccess')}</p>
                  <ul className="mt-1 flex flex-wrap gap-1">
                    {preview.losesAccess.map((u) => (
                      <li key={u.id}>
                        <UserChip user={u} />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-fg-muted">{t('requests.move.nobodyLoses')}</p>
              )}
              {preview.assigneeLosesAccess ? <p className="text-danger">{t('requests.move.reassignFirst')}</p> : null}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => (setTarget(null), setPreview(null))}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={busy}
                disabled={preview.assigneeLosesAccess}
                onClick={async () => {
                  setBusy(true);
                  const ok = await onMove(target.id);
                  setBusy(false);
                  if (ok) {
                    setTarget(null);
                    setPreview(null);
                  }
                }}
              >
                {t('requests.move.confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

/** Triage aid (Section 14): similar readable requests, with a shortcut to mark this one a duplicate. */
function DuplicateSuggestions({ request, onMark }: { request: RequestView; onMark: (candidate: { id: string; key: string; title: string }) => void }) {
  const { t } = useTranslation();
  const candidates = useApiQuery('getRequestDuplicateCandidates', { params: { id: request.id } });
  const list = candidates.data?.data ?? [];
  return (
    <Card className="px-4 py-3">
      <h2 className="text-sm font-semibold">{t('requests.duplicates.title')}</h2>
      <p className="mt-0.5 text-xs text-fg-subtle">{list.length ? t('requests.duplicates.hint') : t('similar.noTextMatches')}</p>
      <ul className="mt-2 divide-y divide-border empty:hidden">
        {list.map((c) => (
          <li key={c.id} className="flex items-center gap-2 py-1.5 text-sm">
            <KeyLabel value={c.key} />
            <Link to={`/requests/${c.key}`} className="min-w-0 flex-1 truncate hover:underline">
              {c.title}
            </Link>
            <RequestStatusBadge status={c.status} />
            <Button size="sm" variant="ghost" onClick={() => onMark({ id: c.id, key: c.key, title: c.title })}>
              {t('requests.duplicates.mark')}
            </Button>
          </li>
        ))}
      </ul>
      <div className="mt-2">
        <AiDuplicateCheck request={request} onMark={onMark} />
      </div>
    </Card>
  );
}

export function ConfirmationPanel({ request }: { request: RequestView }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  // Links from the resolution email preselect an answer visually; only a click records it.
  const [params] = useSearchParams();
  const suggested = params.get('respond');
  const confirm = useApiMutation('confirmRequest', { invalidate: ['getRequest', 'listRequests', 'listComments'] });
  const current = request.currentConfirmation;
  if (!current) return null;
  const respond = async (outcome: 'CONFIRMED' | 'PERSISTS') => {
    try {
      await confirm.mutateAsync({ params: { id: request.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: request.version, fulfillmentGeneration: current.generation, outcome, ...(comment.trim() ? { commentMarkdown: comment } : {}) } });
      toast.success(outcome === 'CONFIRMED' ? t('requests.confirm.thanks') : t('requests.confirm.reported'));
      setComment('');
    } catch (error) {
      toastError(error);
    }
  };
  if (current.outcome !== 'UNCONFIRMED') {
    return (
      <Card className="flex items-center gap-2 px-4 py-3 text-sm">
        {current.outcome === 'CONFIRMED' ? <CheckCircle2 className="size-4 text-success" /> : <X className="size-4 text-danger" />}
        {t(`status.confirmation.${current.outcome}`)} · {t('requests.confirm.generation', { generation: current.generation })}
      </Card>
    );
  }
  return (
    <Card id="confirm" className={`scroll-mt-4 border-accent/40 px-4 py-3 ${suggested ? 'ring-2 ring-accent/40' : ''}`}>
      <h2 className="text-sm font-semibold">{t('requests.confirm.title')}</h2>
      <p className="mt-0.5 text-sm text-fg-muted">{t('requests.confirm.body')}</p>
      {suggested ? <p className="mt-1 text-xs text-fg-subtle">{t('requests.confirm.fromEmail')}</p> : null}
      <Textarea className="mt-3" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('requests.confirm.commentPlaceholder')} rows={2} />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant={suggested === 'persists' ? 'secondary' : 'primary'} autoFocus={suggested === 'confirmed'} onClick={() => void respond('CONFIRMED')} loading={confirm.isPending}>
          <ThumbsUp /> {t('requests.confirm.confirmed')}
        </Button>
        <Button variant={suggested === 'persists' ? 'primary' : 'secondary'} autoFocus={suggested === 'persists'} onClick={() => void respond('PERSISTS')} loading={confirm.isPending}>
          <ThumbsDown /> {t('requests.confirm.persists')}
        </Button>
        <span className="ml-auto self-center text-xs text-fg-subtle">{t('requests.confirm.generation', { generation: current.generation })}</span>
      </div>
    </Card>
  );
}

function LinkedWork({ request }: { request: RequestView }) {
  const { t } = useTranslation();
  const work = useApiQuery('getRequestWork', { params: { id: request.id } });
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [q, setQ] = useState('');
  const [required, setRequired] = useState(true);
  const search = useApiQuery('listWorkItems', { query: { q, limit: '8' } }, { enabled: open && q.trim().length >= 2 });
  const link = useApiMutation('linkRequestWork', { invalidate: ['getRequest', 'getRequestWork'] });
  const unlink = useApiMutation('unlinkRequestWork', { invalidate: ['getRequest', 'getRequestWork'] });
  const canLink = request.availableActions.includes('linkWork');
  return (
    <div className="flex flex-col gap-3">
      {canLink ? (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            <Link2 /> {t('requests.detail.linkWork')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setCreateOpen(true)}>
            <Plus /> {t('nav.newWork')}
          </Button>
        </div>
      ) : null}
      <QueryState query={work} empty={work.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">{t('requests.detail.linkedWorkEmpty')}</p> : undefined}>
        <ul className="flex flex-col gap-1.5">
          {(work.data?.data ?? []).map((w) => (
            <li key={w.id} className="flex items-center gap-2 rounded-[var(--radius)] border border-border px-3 py-2 text-sm">
              <KeyLabel value={w.key} />
              <Link to={`/work/${w.key}`} className="min-w-0 flex-1 truncate font-medium hover:underline">
                {w.title}
              </Link>
              <PhaseBadge phase={w.phase} />
              <Badge tone="outline">{w.required ? t('requests.detail.required') : t('requests.detail.optional')}</Badge>
              {canLink ? (
                <button aria-label={t('requests.detail.unlink')} className="text-fg-subtle hover:text-danger" onClick={() => void unlink.mutateAsync({ params: { id: request.id, childId: w.id } }).catch(toastError)}>
                  <X className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </QueryState>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={t('requests.detail.linkWork')}>
          <div className="flex flex-col gap-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('requests.detail.linkWorkSearch')} autoFocus />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={required} onCheckedChange={(v) => setRequired(v === true)} /> {t('requests.detail.required')}
            </label>
            <ul className="max-h-64 overflow-y-auto">
              {(search.data?.data ?? []).map((w) => (
                <li key={w.id}>
                  <button
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                    onClick={async () => {
                      try {
                        await link.mutateAsync({ params: { id: request.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: request.version, workItemId: w.id, required } });
                        setOpen(false);
                      } catch (error) {
                        toastError(error);
                      }
                    }}
                  >
                    <KeyLabel value={w.key} /> <span className="truncate">{w.title}</span> <PhaseBadge phase={w.phase} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
      <CreateWorkDialog open={createOpen} onOpenChange={setCreateOpen} defaults={{ requestId: request.id, title: request.title, ...(request.project ? { projectId: request.project.id } : {}), ...(request.triageTeam ? { teamId: request.triageTeam.id } : {}) }} />
    </div>
  );
}

function Decisions({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useApiQuery('getRequestDecisions', { params: { id } });
  return (
    <QueryState query={query}>
      <ol className="flex flex-col gap-2 border-l border-border pl-4">
        {(query.data?.data ?? []).map((d) => (
          <li key={d.id} className="text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{t(`actions.request.${d.action as 'accept'}`, { defaultValue: d.action })}</span>
              <RequestStatusBadge status={d.toStatus} />
            </div>
            {d.reason ? <p className="text-fg-muted">{d.reason}</p> : null}
            <p className="text-xs text-fg-subtle">
              {d.actor?.displayName ?? 'System'} · <TimeAgo iso={d.occurredAt} />
            </p>
          </li>
        ))}
      </ol>
    </QueryState>
  );
}

const SIMPLE_REQUEST_ACTIONS: readonly RequestAction[] = ['accept', 'submit'];

/** Triage decisions with the inputs each guard requires. */
function RequestTransitionDialog({ request, action, initialCanonical = null, onClose }: { request: RequestView; action: RequestAction; initialCanonical?: { id: string; title: string; key: string } | null; onClose: () => void }) {
  const { t } = useTranslation();
  const transition = useApiMutation('transitionRequest', { invalidate: ['getRequest', 'listRequests', 'getRequestDecisions', 'listComments'] });
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [canonical, setCanonical] = useState<{ id: string; title: string; key: string } | null>(initialCanonical);
  const [q, setQ] = useState('');
  const search = useApiQuery('listRequests', { query: { q, limit: '6', view: 'all' } }, { enabled: action === 'mark_duplicate' && q.trim().length >= 2 });
  const needsReason = ['reject', 'mark_duplicate', 'cancel', 'reconsider', 'accept_correction', 'fulfill_after_review'].includes(action) && !(action === 'cancel' && request.status === 'DRAFT');

  const submit = async () => {
    try {
      await transition.mutateAsync({
        params: { id: request.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          expectedVersion: request.version,
          action,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(action === 'ask_information' ? { requestedInformation: text } : {}),
          ...(action === 'answer_information' ? { answerMarkdown: text } : {}),
          ...(action === 'mark_duplicate' && canonical ? { canonicalRequestId: canonical.id } : {}),
          ...(action === 'fulfill' && text.trim() ? { directFulfillment: { outcomeMarkdown: text } } : {}),
        },
      });
      toast.success(t(`actions.request.${action}`));
      onClose();
    } catch (error) {
      toastError(error);
    }
  };

  const titleKey = action === 'reject' ? 'rejectTitle' : action === 'mark_duplicate' ? 'duplicateTitle' : action === 'ask_information' ? 'askTitle' : action === 'fulfill' ? 'fulfillTitle' : action === 'fulfill_after_review' ? 'reviewTitle' : action === 'accept_correction' ? 'correctionTitle' : action === 'cancel' ? 'cancelTitle' : 'reconsiderTitle';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t(`requests.dialogs.${titleKey}`)}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {action === 'ask_information' ? (
            <Field label={t('requests.dialogs.askPlaceholder')} required>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} autoFocus />
            </Field>
          ) : null}
          {action === 'fulfill' ? (
            <Field label={t('requests.dialogs.fulfillDirect')} hint={request.fulfillment.stage === 'DELIVERED' ? undefined : t('status.fulfillment.NONE_LINKED')}>
              <MarkdownEditor value={text} onChange={setText} minRows={3} />
            </Field>
          ) : null}
          {action === 'mark_duplicate' ? (
            <Field label={t('requests.dialogs.duplicateSearch')} required>
              <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
              <ul className="max-h-48 overflow-y-auto">
                {(search.data?.data ?? [])
                  .filter((r) => r.id !== request.id)
                  .map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => setCanonical({ id: r.id, title: r.title, key: r.key })} className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${canonical?.id === r.id ? 'bg-accent-soft' : ''}`}>
                        <KeyLabel value={r.key} /> <span className="truncate">{r.title}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            </Field>
          ) : null}
          {needsReason ? (
            <Field label={t('common.reason')} required>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={action === 'fulfill_after_review' ? t('requests.dialogs.reviewPlaceholder') : t('common.reasonPlaceholder')} />
            </Field>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant={['reject', 'cancel'].includes(action) ? 'danger' : 'primary'} loading={transition.isPending} disabled={(needsReason && !reason.trim()) || (action === 'ask_information' && !text.trim()) || (action === 'mark_duplicate' && !canonical)}>
              {t(`actions.request.${action}`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
